import {
  useEffect,
  useRef,
  type ComponentType,
  type ReactElement,
} from 'react';
import { LoadingShell } from '@shared/components/LoadingShell';
import {
  publishLoginErrorEncounteredEvent,
  publishSignupErrorEncounteredEvent,
  type AuthFlow,
  type AuthOtpRequestTrigger,
} from '@shared/utils/analytics/events';
import { reportClientError } from '@shared/utils/reportClientError';
import { PHONE_SMS_OTP_AUTH_INTERACTION_IDS } from './interactions';
import {
  isPhoneSmsOtpAuthScreen,
  PhoneSmsOtpAuthScreenNames,
} from './screenMapping';
import type {
  OTPVerificationScreenProps,
  PhoneCountryCode,
  PhoneLoginScreenProps,
} from '../universal-login/types/descope';

type DescopeNext = (
  interactionId: string,
  payload?: Record<string, unknown>
) => Promise<unknown>;

type DescopeRecord = Record<string, unknown>;

type DescopeFlowState = DescopeRecord & {
  screenName?: string;
  error?: { text?: string };
  next?: DescopeNext;
};

export interface PhoneSmsOtpAuthRouteScreenProps {
  screenName?: string;
  state: DescopeFlowState;
  /**
   * Which entry point started this SMS leg. The same sub-flow is nested under
   * both the login and signup pages, so its screen names cannot say — the
   * caller has to.
   */
  authFlow?: AuthFlow;
  formWithoutSmsAuthFlow: DescopeRecord;
  smsAuthForm: DescopeRecord;
  smsScreenContext: DescopeRecord;
  pendingPhone?: string;
  onPendingPhoneUpdate?: (phone: string | undefined) => void;
  onAutoSubmitPendingPhoneUpdate?: (pending: boolean) => void;
  autoSubmitPendingPhone?: boolean;
  newDesign?: boolean;
  smsAuthAllowedCountries: PhoneCountryCode[];
  onFormUpdate: (data: DescopeRecord) => void;
  onPhoneUpdate: (phone: string | undefined) => void;
  onPhoneLoginBack?: () => boolean | Promise<boolean>;
  resetError: () => void;
  consumeInitialOtpChallengeRequestTrigger?: () =>
    AuthOtpRequestTrigger | undefined;
  PhoneLoginScreen: ComponentType<PhoneLoginScreenProps>;
  OtpVerificationScreen: ComponentType<OTPVerificationScreenProps>;
  errorText?: string;
}

export const routePhoneSmsOtpAuthScreen = ({
  screenName,
  state,
  authFlow = 'login',
  formWithoutSmsAuthFlow,
  smsAuthForm,
  smsScreenContext,
  pendingPhone,
  onPendingPhoneUpdate,
  onAutoSubmitPendingPhoneUpdate,
  autoSubmitPendingPhone = false,
  newDesign = false,
  smsAuthAllowedCountries,
  onFormUpdate,
  onPhoneUpdate,
  onPhoneLoginBack,
  resetError,
  consumeInitialOtpChallengeRequestTrigger,
  PhoneLoginScreen,
  OtpVerificationScreen,
  errorText,
}: PhoneSmsOtpAuthRouteScreenProps): ReactElement | null => {
  if (!screenName || !isPhoneSmsOtpAuthScreen(screenName)) {
    return null;
  }

  if (screenName === PhoneSmsOtpAuthScreenNames.PHONE_LOGIN_SCREEN) {
    if (pendingPhone && autoSubmitPendingPhone && !state.error?.text) {
      return (
        <PreCollectedPhoneSubmit
          onSubmit={async () => {
            const nextForm = { ...smsAuthForm, phone: pendingPhone };
            onPhoneUpdate(pendingPhone);
            onFormUpdate(formWithoutSmsAuthFlow);
            try {
              await state.next?.(
                PHONE_SMS_OTP_AUTH_INTERACTION_IDS.SUBMIT_PHONE,
                nextForm
              );
            } catch (error) {
              if (authFlow === 'signup') {
                publishSignupErrorEncounteredEvent(
                  'SMS',
                  error instanceof Error ? error.message : 'Signup failed'
                );
              } else {
                publishLoginErrorEncounteredEvent(
                  'SMS',
                  error instanceof Error ? error.message : 'Login failed'
                );
              }
              reportClientError('Pre-collected phone submit failed', error, {
                flowId: 'universal-login',
                handler: 'pre-collected-phone-submit',
                screenName: PhoneSmsOtpAuthScreenNames.PHONE_LOGIN_SCREEN,
                source: 'phone-sms-otp-auth',
              });
            } finally {
              onAutoSubmitPendingPhoneUpdate?.(false);
            }
          }}
        />
      );
    }
    if (authFlow === 'signup' && newDesign) {
      return null;
    }
    return (
      <PhoneLoginScreen
        authFlow={authFlow}
        allowedCountries={smsAuthAllowedCountries}
        defaultCountry="US"
        onClickContinue={async (phone: string) => {
          const nextForm = { ...smsAuthForm, phone };
          onPhoneUpdate(phone);
          onFormUpdate(formWithoutSmsAuthFlow);
          if (state.next) {
            await state.next(
              PHONE_SMS_OTP_AUTH_INTERACTION_IDS.SUBMIT_PHONE,
              nextForm
            );
          }
        }}
        onBackButtonClick={async () => {
          onPendingPhoneUpdate?.(undefined);
          onPhoneUpdate(undefined);
          if (await onPhoneLoginBack?.()) {
            return;
          }

          onFormUpdate(formWithoutSmsAuthFlow);
          if (state.next) {
            await state.next(
              PHONE_SMS_OTP_AUTH_INTERACTION_IDS.CHOOSE_ANOTHER_AUTH_METHOD,
              formWithoutSmsAuthFlow
            );
          }
        }}
        errorText={errorText ?? state?.error?.text}
        context={smsScreenContext}
        resetError={resetError}
      />
    );
  }

  return (
    <OtpVerificationScreen
      authFlow={authFlow}
      otpChannel="sms"
      newDesign={newDesign && authFlow === 'signup'}
      consumeInitialOtpChallengeRequestTrigger={
        consumeInitialOtpChallengeRequestTrigger
      }
      onFormUpdate={onFormUpdate}
      onOTPVerificationClick={async (code: string) => {
        if (state.next) {
          await state.next(PHONE_SMS_OTP_AUTH_INTERACTION_IDS.VERIFY_CODE, {
            ...smsAuthForm,
            code,
          });
        }
      }}
      onClickResend={async () => {
        if (state.next) {
          await state.next(
            PHONE_SMS_OTP_AUTH_INTERACTION_IDS.RESEND,
            smsAuthForm
          );
        }
      }}
      onClickUseDifferentNumber={async () => {
        onPendingPhoneUpdate?.(undefined);
        onAutoSubmitPendingPhoneUpdate?.(false);
        onPhoneUpdate(undefined);
        if (state.next) {
          await state.next(
            PHONE_SMS_OTP_AUTH_INTERACTION_IDS.USE_DIFFERENT_NUMBER,
            smsAuthForm
          );
        }
      }}
      onBackButtonClick={
        newDesign && authFlow === 'signup'
          ? async () => {
              if (state.next) {
                await state.next(
                  PHONE_SMS_OTP_AUTH_INTERACTION_IDS.USE_DIFFERENT_NUMBER,
                  smsAuthForm
                );
              }
            }
          : undefined
      }
      onClickChooseAnotherAuthMethod={
        newDesign && authFlow === 'signup'
          ? undefined
          : async () => {
              if (state.next) {
                await state.next(
                  PHONE_SMS_OTP_AUTH_INTERACTION_IDS.CHOOSE_ANOTHER_AUTH_METHOD,
                  smsAuthForm
                );
              }
            }
      }
      errorText={errorText ?? state?.error?.text}
      rawErrorText={state?.error?.text}
      context={smsScreenContext}
      resetError={resetError}
    />
  );
};

// eslint-disable-next-line react-refresh/only-export-components
const PreCollectedPhoneSubmit = ({
  onSubmit,
}: {
  onSubmit: () => Promise<void>;
}): ReactElement => {
  const submitted = useRef(false);
  useEffect(() => {
    if (!submitted.current) {
      submitted.current = true;
      void onSubmit();
    }
  }, [onSubmit]);
  return <LoadingShell />;
};
